import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Megaphone, TrendingUp, IndianRupee, MousePointerClick, Eye, ShoppingCart, X, Calendar, Plus, RefreshCw } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { getCampaigns, createCampaign as createCampaignSvc, updateCampaign as updateCampaignSvc, upsertMetaCampaign } from '../services/campaigns.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import { useDateRange } from '../context/DateRangeContext'
import type { Campaign } from '../types'

// ── Add Campaign Modal ─────────────────────────────────────────
function AddCampaignModal({ onClose, onAdd }: { onClose: () => void; onAdd: (c: Omit<Campaign, 'id'>) => void }) {
  const today = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({
    name: '', clientName: '',
    type: 'Google Ads' as Campaign['type'],
    budget: '', startDate: today,
  })
  const [error, setError] = useState('')
  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim())  { setError('Campaign name is required.'); return }
    const budget = parseFloat(form.budget.replace(/[^0-9.]/g, ''))
    if (!budget || budget <= 0) { setError('Enter a valid budget.'); return }
    onAdd({
      name: form.name.trim(),
      clientId: '',
      clientName: form.clientName.trim(),
      type: form.type,
      status: 'active',
      budget,
      spend: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      revenue: 0,
      roas: 0,
      startDate: form.startDate,
    })
    onClose()
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }
  const focusOn  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')
  const focusOff = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white font-syne">New Campaign</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CAMPAIGN NAME *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Astra Fintech — Q3 Google Search"
              className={inputCls} style={inputStyle} autoFocus onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CLIENT</label>
              <input value={form.clientName} onChange={e => set('clientName', e.target.value)}
                placeholder="Client name" className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>TYPE</label>
              <select value={form.type} onChange={e => set('type', e.target.value)} className={inputCls} style={inputStyle}>
                {(['Google Ads', 'Meta Ads', 'SEO', 'Email', 'Social'] as const).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>MONTHLY BUDGET (₹) *</label>
              <input value={form.budget} onChange={e => set('budget', e.target.value)}
                placeholder="e.g. 80000" className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>START DATE</label>
              <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Launch Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

type FilterType = 'All' | Campaign['type']
const FILTERS: FilterType[] = ['All', 'Google Ads', 'Meta Ads', 'SEO', 'Email', 'Social']

function formatINR(n: number) {
  return '₹' + n.toLocaleString('en-IN')
}

function roasColor(roas: number) {
  if (roas >= 3) return '#e8a000'
  if (roas >= 1.5) return '#ff8c00'
  return '#cc3300'
}

function TypeBadge({ type }: { type: Campaign['type'] }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'rgba(255,198,0,0.12)', color: '#ffc600' }}>
      {type}
    </span>
  )
}

// ── Campaign Detail Modal ──────────────────────────────────────
function CampaignDetailModal({ campaign: c, onClose, onStatusChange }: {
  campaign: Campaign; onClose: () => void
  onStatusChange: (id: string, status: Campaign['status']) => void
}) {
  const spendPct = c.budget > 0 ? Math.min((c.spend / c.budget) * 100, 100) : 0
  const ctr = c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(2) : '0'
  const cpc = c.clicks > 0 ? Math.round(c.spend / c.clicks) : 0
  const isActive = c.status === 'active'
  const isPaused = c.status === 'paused'
  const isEnded = c.status === 'ended'
  const statusStyle =
    c.status === 'active' ? { bg: 'rgba(50,200,100,0.12)', color: '#32c864' } :
    c.status === 'paused' ? { bg: 'rgba(255,140,0,0.12)', color: '#ff8c00' } :
    c.status === 'ended'  ? { bg: 'rgba(85,85,85,0.2)',   color: '#555' } :
    { bg: 'rgba(85,85,85,0.2)', color: '#555' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Header */}
        <div className="p-5 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1 mr-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: isActive ? '#32c864' : '#555' }} />
                <span className="text-xs font-medium capitalize" style={statusStyle}>{c.status}</span>
                <TypeBadge type={c.type} />
              </div>
              <h2 className="text-base font-bold text-white font-syne leading-snug">{c.name}</h2>
              <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{c.clientName}</p>
            </div>
            <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Budget bar */}
          <div className="rounded-xl p-4" style={{ background: '#1a1a1a' }}>
            <div className="flex justify-between text-sm mb-2">
              <span style={{ color: '#a0a0a0' }}>Spend</span>
              <span className="text-white font-semibold">{formatINR(c.spend)} <span style={{ color: '#555' }}>/ {formatINR(c.budget)}</span></span>
            </div>
            <div className="h-2 rounded-full" style={{ background: '#2a2a2a' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${spendPct}%`, background: spendPct > 90 ? '#cc3300' : '#ffc600' }} />
            </div>
            <p className="text-xs mt-1.5" style={{ color: '#555' }}>{spendPct.toFixed(0)}% of budget used</p>
          </div>

          {/* ROAS big number */}
          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(232,160,0,0.06)', border: '1px solid rgba(232,160,0,0.2)' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: '#555' }}>RETURN ON AD SPEND</p>
            <p className="text-4xl font-bold font-syne" style={{ color: roasColor(c.roas) }}>{c.roas.toFixed(2)}x</p>
            <p className="text-xs mt-1" style={{ color: '#555' }}>Revenue: <span className="font-semibold" style={{ color: '#e8a000' }}>{formatINR(c.revenue)}</span></p>
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Impressions', value: (c.impressions / 1000).toFixed(1) + 'k', icon: Eye, color: '#a0a0a0' },
              { label: 'Clicks', value: c.clicks.toLocaleString('en-IN'), icon: MousePointerClick, color: '#ffc600' },
              { label: 'CTR', value: ctr + '%', icon: TrendingUp, color: '#ff8c00' },
              { label: 'CPC', value: '₹' + cpc, icon: IndianRupee, color: '#e8a000' },
              { label: 'Conversions', value: c.conversions.toString(), icon: ShoppingCart, color: '#32c864' },
              { label: 'Conv. Rate', value: c.clicks > 0 ? ((c.conversions / c.clicks) * 100).toFixed(1) + '%' : '—', icon: TrendingUp, color: '#ffc600' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="rounded-lg px-3 py-2.5 flex items-center gap-2.5" style={{ background: '#1a1a1a' }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                  <Icon size={13} style={{ color }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{value}</p>
                  <p className="text-xs" style={{ color: '#555' }}>{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Dates */}
          <div className="flex items-center gap-3 text-xs" style={{ color: '#555' }}>
            <Calendar size={12} />
            <span>Started {new Date(c.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            {c.endDate && <><span>·</span><span>Ends {new Date(c.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></>}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
            style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
            Close
          </button>
          {isActive && (
            <button onClick={() => { onStatusChange(c.id, 'paused'); onClose() }}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: 'rgba(255,140,0,0.12)', color: '#ff8c00', border: '1px solid rgba(255,140,0,0.3)' }}>
              ⏸ Pause
            </button>
          )}
          {isPaused && (
            <button onClick={() => { onStatusChange(c.id, 'active'); onClose() }}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: 'rgba(50,200,100,0.12)', color: '#32c864', border: '1px solid rgba(50,200,100,0.3)' }}>
              ▶ Resume
            </button>
          )}
          {!isEnded && (
            <button onClick={() => { onStatusChange(c.id, 'ended'); onClose() }}
              className="flex-shrink-0 px-3 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.2)' }}>
              ✓ End
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg p-3 text-xs" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.25)', color: '#fff' }}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {formatINR(p.value)}
        </p>
      ))}
    </div>
  )
}

const LS_LAST_SYNC = 'erp_meta_last_sync'

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [filter, setFilter]       = useState<FilterType>('All')
  const [selected, setSelected]   = useState<Campaign | null>(null)
  const [showAdd, setShowAdd]     = useState(false)
  const [syncing, setSyncing]     = useState(false)
  const [lastSync, setLastSync]   = useState<string | null>(() => {
    try { return localStorage.getItem(LS_LAST_SYNC) } catch { return null }
  })
  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const isAdmin         = useIsAdmin()
  const { range }       = useDateRange()
  const location        = useLocation()

  useEffect(() => {
    getCampaigns().then(setCampaigns).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  const handleMetaSync = async (forceRefresh = false) => {
    setSyncing(true)
    try {
      const url = `/api/meta-insights${forceRefresh ? '?refresh=1' : ''}`
      const res  = await fetch(url)
      const json = await res.json()

      if (!res.ok) {
        toast(json.error || 'Meta sync failed', 'error')
        return
      }

      const metaCampaigns: any[] = json.campaigns || []
      let created = 0, updated = 0

      for (const mc of metaCampaigns) {
        const existing = campaigns.find(c => c.metaCampaignId === mc.metaCampaignId)
        const payload = {
          name:           mc.name,
          clientId:       '',
          clientName:     existing?.clientName || '',
          type:           'Meta Ads' as const,
          status:         mc.status,
          budget:         mc.budget,
          spend:          mc.spend,
          impressions:    mc.impressions,
          clicks:         mc.clicks,
          conversions:    mc.conversions,
          revenue:        mc.revenue,
          roas:           mc.roas,
          startDate:      mc.startDate,
          endDate:        mc.endDate,
          metaCampaignId: mc.metaCampaignId,
        }
        const saved = await upsertMetaCampaign(payload)
        if (existing) {
          setCampaigns(prev => prev.map(c => c.metaCampaignId === mc.metaCampaignId ? saved : c))
          updated++
        } else {
          setCampaigns(prev => [saved, ...prev])
          created++
        }
      }

      const now = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      try { localStorage.setItem(LS_LAST_SYNC, now) } catch {}
      setLastSync(now)

      const msg = json.cached
        ? `Showing cached data · ${metaCampaigns.length} campaigns`
        : `Synced ${metaCampaigns.length} campaigns (${created} new, ${updated} updated)`
      toast(msg, 'success')

      addActivity({
        type: 'meta_sync', emoji: '📊',
        description: `Meta Ads synced — ${metaCampaigns.length} campaigns`,
        detail: `${created} new · ${updated} updated`,
      })
    } catch (e: any) {
      toast(e.message || 'Meta sync failed', 'error')
    } finally {
      setSyncing(false)
    }
  }

  // Show campaigns that overlap with the selected date range.
  // A campaign is visible if it was running at any point during the range —
  // i.e. it started before the range ended AND it ended after the range started (or is still active).
  const dateCampaigns = campaigns.filter(c => {
    if (!range.start && !range.end) return true
    if (!c.startDate) return true
    const cStart = new Date(c.startDate)
    if (isNaN(cStart.getTime())) return true
    const cEnd = c.endDate ? new Date(c.endDate) : null
    if (range.end   && cStart > range.end)   return false  // not started yet within range
    if (range.start && cEnd && cEnd < range.start) return false  // already ended before range
    return true
  })
  const filtered = dateCampaigns.filter(c => filter === 'All' || c.type === filter)

  const handleStatusChange = async (id: string, status: Campaign['status']) => {
    try { await updateCampaignSvc(id, { status }) } catch { toast('Update failed', 'error'); return }
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status } : c))
    setSelected(prev => prev?.id === id ? { ...prev, status } : prev)
    const camp = campaigns.find(c => c.id === id)
    if (camp) {
      const msg = status === 'paused' ? `"${camp.name}" paused` : status === 'active' ? `"${camp.name}" resumed` : `"${camp.name}" ended`
      const toastType = status === 'paused' ? 'info' : 'success'
      toast(msg, toastType)
      addActivity({
        type: 'campaign_status',
        emoji: status === 'paused' ? '⏸' : status === 'active' ? '▶️' : '🏁',
        description: msg,
        detail: `${camp.type} · ${camp.clientName}`,
      })
    }
  }

  const totalSpend = filtered.reduce((s, c) => s + c.spend, 0)
  const totalRevenue = filtered.reduce((s, c) => s + c.revenue, 0)
  const avgRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0

  const chartData = filtered.slice(0, 8).map(c => ({
    name: c.name.length > 18 ? c.name.slice(0, 18) + '…' : c.name,
    Spend: c.spend,
    Revenue: c.revenue,
  }))

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Campaigns</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{filtered.length} campaigns</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Meta Sync button */}
          <div className="flex flex-col items-end gap-0.5">
            <button
              onClick={() => handleMetaSync(false)}
              disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
              style={{ background: '#1a1a1a', color: '#ffc600', border: '1px solid rgba(255,198,0,0.3)' }}
              onMouseEnter={e => { if (!syncing) e.currentTarget.style.borderColor = 'rgba(255,198,0,0.6)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)' }}
              title="Sync live KPIs from Meta Ads (cached 1hr) — right-click to force refresh"
              onContextMenu={e => { e.preventDefault(); handleMetaSync(true) }}
            >
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing…' : 'Sync Meta'}
            </button>
            {lastSync && <span className="text-xs" style={{ color: '#555' }}>Last: {lastSync}</span>}
          </div>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              <Plus size={15} /> New Campaign
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Spend', value: formatINR(totalSpend), icon: IndianRupee },
          { label: 'Total Revenue', value: formatINR(totalRevenue), icon: TrendingUp },
          { label: 'Avg ROAS', value: avgRoas.toFixed(2) + 'x', icon: ShoppingCart },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={15} style={{ color: '#ffc600' }} />
              <p className="text-xs" style={{ color: '#a0a0a0' }}>{label}</p>
            </div>
            <p className="text-xl font-bold font-syne text-white">{value}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="rounded-xl p-4 mb-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <p className="text-sm font-semibold text-white mb-4">Spend vs Revenue by Campaign</p>
          <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 44)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30, top: 4, bottom: 4 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,198,0,0.06)" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#a0a0a0', fontSize: 11 }} tickFormatter={v => '₹' + (v / 1000) + 'k'} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#a0a0a0', fontSize: 10 }} width={150} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Spend" fill="rgba(255,198,0,0.25)" radius={[0, 4, 4, 0]} maxBarSize={16} />
              <Bar dataKey="Revenue" fill="#ffc600" radius={[0, 4, 4, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
            style={filter === f
              ? { background: '#ffc600', color: '#000' }
              : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Campaign cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16" style={{ color: '#555' }}>No campaigns found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(c => {
            const spendPct = c.budget > 0 ? Math.min((c.spend / c.budget) * 100, 100) : 0
            return (
              <div key={c.id} className="rounded-xl p-4 transition-colors cursor-pointer" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}
                onClick={() => setSelected(c)}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)')}>
                {/* Title row */}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0`} style={{ background: c.status === 'active' ? '#e8a000' : '#555555' }} />
                      <p className="text-sm font-semibold text-white">{c.name}</p>
                    </div>
                    <p className="text-xs ml-4" style={{ color: '#a0a0a0' }}>{c.clientName}</p>
                  </div>
                  <TypeBadge type={c.type} />
                </div>

                {/* Budget bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1" style={{ color: '#a0a0a0' }}>
                    <span>Spend: {formatINR(c.spend)}</span>
                    <span>Budget: {formatINR(c.budget)}</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: '#1a1a1a' }}>
                    <div className="h-full rounded-full" style={{ width: `${spendPct}%`, background: '#ffc600' }} />
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[
                    { label: 'Impressions', value: (c.impressions / 1000).toFixed(1) + 'k', icon: Eye },
                    { label: 'Clicks', value: c.clicks.toLocaleString('en-IN'), icon: MousePointerClick },
                    { label: 'Conv.', value: c.conversions.toString(), icon: ShoppingCart },
                    { label: 'ROAS', value: c.roas.toFixed(2) + 'x', icon: TrendingUp, colored: true },
                  ].map(({ label, value, icon: Icon, colored }) => (
                    <div key={label} className="rounded-lg p-2" style={{ background: '#1a1a1a' }}>
                      <Icon size={12} className="mx-auto mb-1" style={{ color: '#a0a0a0' }} />
                      <p className="text-xs font-semibold" style={{ color: colored ? roasColor(c.roas) : '#fff' }}>{value}</p>
                      <p className="text-xs" style={{ color: '#555', fontSize: '10px' }}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {selected && <CampaignDetailModal campaign={selected} onClose={() => setSelected(null)} onStatusChange={handleStatusChange} />}
      {showAdd && (
        <AddCampaignModal
          onClose={() => setShowAdd(false)}
          onAdd={async c => {
            try {
              const saved = await createCampaignSvc(c)
              setCampaigns(prev => [saved, ...prev])
              toast(`"${saved.name}" campaign launched!`, 'success')
              addActivity({ type: 'campaign_launched', emoji: '📣', description: `Campaign launched: ${saved.name}`, detail: `${saved.type} · ₹${saved.budget.toLocaleString('en-IN')} budget` })
            } catch { toast('Failed to create campaign', 'error') }
          }}
        />
      )}
    </div>
  )
}
